"use client";

import { Button } from "@/components/ui/button";
import React, { useState, useEffect } from "react";
import useAxiosAuth from "@/lib/hooks/useAxiosAuth";
import toast from "react-hot-toast";
import { Loader2, Check, Calendar, Zap } from "lucide-react";
import { useSession } from "next-auth/react";
import { reworkDate } from "@/lib/helpers/reworkDate";
import CWFResponsiveDialog from "@/components/ui/CWFResponsiveDialog";
import { Badge } from "@/components/ui/badge";

export default function DiagoSubscriptionButton({
                                                    label = "Souscrire à Diago",
                                                    className = "",
                                                    variant = "default",
                                                    size = "default",
                                                    showStatus = true,
                                                    idProfile,
                                                }) {
    const { data: session, status: authStatus } = useSession();
    const axios = useAxiosAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [diagoSubscription, setDiagoSubscription] = useState(null);
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);

    // Plans disponibles
    const plans = [
        {
            id: 'monthly',
            name: 'Mensuel',
            price: '10.00',
            period: 'mois',
            description: 'Facturation mensuelle',
            popular: false,
            icon: Calendar,
            savings: null
        },
        {
            id: 'annual',
            name: 'Annuel',
            price: '100.00',
            period: 'an',
            description: 'Facturation annuelle',
            popular: true,
            icon: Zap,
            savings: '20%',
            pricePerMonth: '8.33'
        }
    ];

    useEffect(() => {
        if (authStatus === "authenticated" && showStatus) {
            checkDiagoStatus();
        } else {
            setIsCheckingStatus(false);
        }
    }, [authStatus, showStatus]);

    const checkDiagoStatus = async () => {
        try {
            const response = await axios.get('recurrents/user');
            const subscriptions = response.data.data;

            const diago = subscriptions?.find(sub =>
                sub.slug === 'diago' &&
                (sub.status === 'active' || sub.status === 'trialing')
            );

            setDiagoSubscription(diago);
        } catch (error) {
            console.error('Erreur lors de la vérification du statut Diago:', error);
        } finally {
            setIsCheckingStatus(false);
        }
    };

    const handleOpenModal = () => {
        if (authStatus !== "authenticated") {
            toast.error("Vous devez être connecté pour souscrire");
            return;
        }
        setIsOpen(true);
    };

    const handleSubscribe = async (planType) => {
        setIsLoading(true);
        setSelectedPlan(planType);

        /*try {
            const response = await axios.post('recurrents/checkout/diago-sub', {
                is_yearly: planType === 'annual',
                success : process.env.NEXT_PUBLIC_URL + '/profile/' + idProfile,
                error : process.env.NEXT_PUBLIC_URL + '/profile/' + idProfile
            });

            const data = response.data.data;

            if (!data || !data.url) {
                throw new Error('URL de paiement non reçue');
            }

            window.location.href = data.url;

        }
        catch (error) {
            console.error('Erreur lors de la souscription Diago:', error);

            if (error.response?.status === 409) {
                toast.error("Vous avez déjà un abonnement actif");
            } else if (error.response?.status === 401) {
                toast.error("Session expirée, veuillez vous reconnecter");
            } else {
                toast.error("Une erreur s'est produite lors de la redirection vers le paiement");
            }

            setIsLoading(false);
            setSelectedPlan(null);
        }*/
    };

    if (isCheckingStatus) {
        return (
            <Button
                disabled
                className={className}
                variant={variant}
                size={size}
            >
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Vérification...
            </Button>
        );
    }

    if (diagoSubscription && showStatus) {
        return (
            <div className="flex items-center justify-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span className="font-medium">
                    Abonné depuis le{' '}
                    <span className="text-primary">
                        {reworkDate(diagoSubscription.current_period_start)}
                    </span>
                </span>
            </div>
        );
    }

    return (
        <>
            <Button
                onClick={handleOpenModal}
                disabled={isLoading || authStatus === "loading"}
                className={className}
                variant={variant}
                size={size}
            >
                {label}
            </Button>

            <CWFResponsiveDialog
                open={isOpen}
                onOpenChange={setIsOpen}
                onCloseChange={setIsOpen}
                trigger={null}
                title="Choisissez votre formule Diago"
                description="Sélectionnez la durée d'abonnement qui vous convient le mieux"
            >
                <div className="space-y-3 py-2 sm:py-4">
                    {plans.map((plan) => {
                        const Icon = plan.icon;
                        const isSelected = selectedPlan === plan.id;
                        const isCurrentlyLoading = isLoading && isSelected;

                        return (
                            <div
                                key={plan.id}
                                className={`relative rounded-lg border-2 p-4 sm:p-6 transition-all cursor-pointer hover:shadow-lg ${
                                    plan.popular
                                        ? 'border-yellow-400 bg-yellow-50'
                                        : 'border-gray-200 hover:border-yellow-200'
                                } ${isSelected ? 'ring-2 ring-yellow-400' : ''}`}
                                onClick={() => !isLoading && handleSubscribe(plan.id)}
                            >
                                {plan.popular && (
                                    <Badge className="absolute -top-2 sm:-top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-xs">
                                        Recommandé
                                    </Badge>
                                )}

                                {/* Layout adaptatif : colonne sur mobile, row sur desktop */}
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                    {/* Contenu principal */}
                                    <div className="flex items-start gap-3 sm:gap-4 flex-1">
                                        <div className={`p-2 sm:p-3 rounded-full shrink-0 ${
                                            plan.popular ? 'bg-yellow-400' : 'bg-gray-100'
                                        }`}>
                                            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${
                                                plan.popular ? 'text-white' : 'text-gray-600'
                                            }`} />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-base sm:text-lg font-semibold mb-1">
                                                {plan.name}
                                            </h3>
                                            <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                                                {plan.description}
                                            </p>

                                            <div className="flex items-baseline gap-2">
                                                <span className="text-2xl sm:text-3xl font-bold">
                                                    {plan.price}€
                                                </span>
                                                <span className="text-sm sm:text-base text-gray-500">
                                                    / {plan.period}
                                                </span>
                                            </div>

                                            {plan.pricePerMonth && (
                                                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                                    Soit {plan.pricePerMonth}€ / mois
                                                </p>
                                            )}

                                            {plan.savings && (
                                                <Badge
                                                    variant="outline"
                                                    className="mt-2 border-green-600 text-green-600 text-xs"
                                                >
                                                    Économisez {plan.savings}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bouton : pleine largeur sur mobile, auto sur desktop */}
                                    <Button
                                        disabled={isLoading}
                                        variant={plan.popular ? "default" : "outline"}
                                        className={`w-full sm:w-auto ${plan.popular ? "bg-yellow-400 hover:bg-yellow-500" : ""}`}
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubscribe(plan.id);
                                        }}
                                    >
                                        {isCurrentlyLoading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Chargement...
                                            </>
                                        ) : (
                                            'Choisir'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Section des avantages avec meilleur responsive */}
                <div className="mt-4 sm:mt-6 rounded-lg bg-gray-50 p-3 sm:p-4">
                    <h4 className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600 shrink-0" />
                        Inclus dans tous les plans :
                    </h4>
                    <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                        <li className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-green-600 shrink-0" />
                            Bilan 360° complet
                        </li>
                        <li className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-green-600 shrink-0" />
                            Suivi de vos performances
                        </li>
                        <li className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-green-600 shrink-0" />
                            Synchronisation automatique
                        </li>
                        <li className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-green-600 shrink-0" />
                            Annulation à tout moment
                        </li>
                    </ul>
                </div>
            </CWFResponsiveDialog>
        </>
    );
}